import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Calendar, Clock, CheckCircle2, Loader2, Building2,
  ExternalLink, ChevronRight, Inbox, XCircle, RefreshCw, AlertCircle, PlayCircle,
} from 'lucide-react';

import { toast } from 'react-toastify';

import { Button, Card, Badge, Banner } from '../../ui-v2';
import { getMisCampanas, cancelarCita, getCondicionesCita } from '../api';
import CondicionesCancelacion from '../compartidos/CondicionesCancelacion';
import ModalVideoTutorial from '../components/ModalVideoTutorial';
import { useAvisoVisto } from '../../../../hooks/useAvisoVisto';

const ESTADO_USUARIO_LABEL = {
  pendiente_reserva: { label: 'Pendiente de reservar' },
  cita_reservada: { label: 'Reservado' },
  realizado: { label: 'Realizado' },
  cancelado_usuario: { label: 'Cancelado' },
  cancelado_sistemas: { label: 'Cancelado por el sistema' },
};

export default function MisCampanas() {
  const navigate = useNavigate();
  const [campanas, setCampanas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Estado del modal de cancelación
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelando, setCancelando] = useState(false);
  const [cancelError, setCancelError] = useState(null);

  // Onboarding: vídeo tutorial (banner + modal, persistencia por persona).
  const { visto, cargando: cargandoAviso, marcarVisto } = useAvisoVisto('onboarding_citas_video');
  const [videoOpen, setVideoOpen] = useState(false);

  // El botón de cabecera abre el vídeo SIN marcar el aviso como visto (acceso
  // permanente). El banner (X o "Ver vídeo") sí lo marca.
  const abrirTutorial = useCallback(() => setVideoOpen(true), []);
  const abrirTutorialDesdeAviso = useCallback(() => {
    setVideoOpen(true);
    marcarVisto();
  }, [marcarVisto]);

  const cargar = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getMisCampanas();
      setCampanas(res.data || []);
    } catch (e) {
      console.error('[MisCampanas]', e);
      setError(e?.response?.data?.error || 'No se pudieron cargar tus campañas.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  // --- Acciones ---

  function pedirCancelar(campana, modoReagendar = false) {
    setCancelError(null);
    setCancelTarget({ campana, reagendar: modoReagendar });
  }

  async function confirmarCancelar() {
    if (!cancelTarget?.campana?.cita) return;
    setCancelando(true);
    setCancelError(null);
    try {
      const res = await cancelarCita(cancelTarget.campana.cita.id);
      const wasReagendar = cancelTarget.reagendar;
      const campanaId = cancelTarget.campana.id;
      setCancelTarget(null);

      // Si supera el límite, no podrá reservar de nuevo: solo recargar lista
      if (res.supera_limite || !wasReagendar) {
        await cargar();
      } else {
        navigate(`/citas-tecnicas/mis-campanas/${campanaId}/reservar`);
      }
    } catch (e) {
      console.error('[cancelarCita]', e);
      // Race (#14b §3.8): las condiciones eran válidas al abrir el modal pero el
      // plazo venció antes de confirmar → el backend responde 409 fuera_de_plazo.
      const msg = e?.response?.data?.error || 'No se pudo cancelar la cita.';
      setCancelError(msg);
      if (e?.response?.status === 409) toast.error(msg);
    } finally {
      setCancelando(false);
    }
  }

  function cerrarCancelar() {
    if (cancelando) return;
    setCancelTarget(null);
    setCancelError(null);
  }

  // --- Render ---

  if (loading) {
    return (
      <div className="px-6 py-12 text-center text-slate-500 text-sm">
        <Loader2 className="w-5 h-5 inline-block animate-spin mr-2" />
        Cargando…
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-6 py-12 max-w-xl mx-auto">
        <div className="border border-red-200 bg-red-50 rounded-lg p-4 text-red-700 text-sm">
          {error}
        </div>
      </div>
    );
  }

  const pendientes = campanas.filter(
    (c) => c.estado === 'Activa' && c.estado_usuario === 'pendiente_reserva'
  );
  const reservadas = campanas.filter(
    (c) => c.estado_usuario === 'cita_reservada' && c.cita
  );
  const otras = campanas.filter(
    (c) => !pendientes.includes(c) && !reservadas.includes(c)
  );

  return (
    <div className="px-6 py-5 min-h-full max-w-4xl">
      <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Mis Citas</h1>
          <p className="text-sm text-slate-500 mt-1">
            Campañas en las que estás incluido y citas reservadas.
          </p>
        </div>
        {/* Acceso permanente al tutorial (NO marca el aviso como visto). */}
        <button
          type="button"
          onClick={abrirTutorial}
          className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 transition-colors self-start sm:self-auto shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-current rounded-md"
        >
          <PlayCircle className="w-4 h-4" aria-hidden="true" />
          Ver tutorial (1 min)
        </button>
      </div>

      {/* Banner de onboarding: solo si sabemos que NO se ha descartado aún. */}
      {!cargandoAviso && !visto && (
        <Banner
          variant="info"
          icon={PlayCircle}
          className="mb-5"
          action={{ label: 'Ver vídeo', onClick: abrirTutorialDesdeAviso }}
          onDismiss={marcarVisto}
        >
          ¿Nuevo por aquí? Mira el vídeo de 1 minuto sobre cómo reservar tu cita.
        </Banner>
      )}

      {campanas.length === 0 && (
        <Card className="p-12 text-center">
          <Inbox className="w-8 h-8 mx-auto mb-3 text-slate-300" />
          <p className="text-sm text-slate-500">
            No estás incluido en ninguna campaña ahora mismo.
          </p>
        </Card>
      )}

      {pendientes.length > 0 && (
        <Seccion
          titulo="Pendientes de reservar"
          subtitulo="Estas campañas requieren que reserves tu cita."
        >
          {pendientes.map((c) => (
            <div key={c.id} className="space-y-1.5">
              {/* Fase 20b · #15 — contexto: volvió a pendiente por un no-show. */}
              {c.ultima_cita_no_asistio && <AvisoNoAsistio cita={c.ultima_cita_no_asistio} />}
              <FilaPendiente
                campana={c}
                onClick={() => navigate(`/citas-tecnicas/mis-campanas/${c.id}/reservar`)}
              />
            </div>
          ))}
        </Seccion>
      )}

      {reservadas.length > 0 && (
        <Seccion titulo="Citas reservadas">
          {reservadas.map((c) => (
            <FilaReservada
              key={c.id}
              campana={c}
              onCancelar={() => pedirCancelar(c, false)}
              onReagendar={() => pedirCancelar(c, true)}
            />
          ))}
        </Seccion>
      )}

      {otras.length > 0 && (
        <Seccion titulo="Histórico" colapsable>
          {otras.map((c) => (
            <FilaHistorico key={c.id} campana={c} />
          ))}
        </Seccion>
      )}

      {cancelTarget && (
        <ModalCancelar
          campana={cancelTarget.campana}
          reagendar={cancelTarget.reagendar}
          loading={cancelando}
          error={cancelError}
          onCancelar={cerrarCancelar}
          onConfirmar={confirmarCancelar}
        />
      )}

      {/* Único modal de vídeo, compartido por el botón de cabecera y el banner. */}
      <ModalVideoTutorial open={videoOpen} onClose={() => setVideoOpen(false)} />
    </div>
  );
}

// --- Subcomponentes ---

function Seccion({ titulo, subtitulo, children, colapsable = false }) {
  const [abierto, setAbierto] = useState(true);
  return (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h2 className="text-sm font-semibold text-slate-800">{titulo}</h2>
          {subtitulo && <p className="text-xs text-slate-500 mt-0.5">{subtitulo}</p>}
        </div>
        {colapsable && (
          <button
            type="button"
            onClick={() => setAbierto((a) => !a)}
            className="text-xs text-slate-500 hover:text-slate-900"
          >
            {abierto ? 'Ocultar' : 'Ver'}
          </button>
        )}
      </div>
      {abierto && <div className="space-y-2">{children}</div>}
    </div>
  );
}

function AmbitoIcon({ ambito }) {
  return ambito === 'colaborador' ? (
    <ExternalLink className="w-3.5 h-3.5 text-slate-400" />
  ) : (
    <Building2 className="w-3.5 h-3.5 text-slate-400" />
  );
}

// Fase 20b · #15 — aviso cuando la participación volvió a pendiente por un
// no-show. Da contexto sobre por qué el usuario vuelve a "Pendientes de reservar".
function AvisoNoAsistio({ cita }) {
  return (
    <div className="bg-amber-100 border border-amber-300 rounded-lg p-3 text-sm flex items-start gap-2">
      <XCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
      <div>
        <p className="font-medium text-amber-900">
          No asististe a tu cita del {formatFecha(cita.fecha)}
        </p>
        <p className="text-xs text-amber-700 mt-1">
          Tu participación sigue activa. Reserva una nueva cita.
        </p>
      </div>
    </div>
  );
}

function FilaPendiente({ campana, onClick }) {
  return (
    <button
      onClick={onClick}
      className="w-full text-left border-2 border-amber-200 bg-amber-50 rounded-lg p-4 hover:border-amber-300 transition-colors"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1">
            <AmbitoIcon ambito={campana.ambito} />
            <span className="font-medium text-slate-900">{campana.titulo}</span>
          </div>
          {campana.descripcion && (
            <p className="text-xs text-slate-600 mt-1 line-clamp-2">{campana.descripcion}</p>
          )}
          <p className="text-xs text-slate-500 mt-2 flex items-center gap-3 flex-wrap">
            <span className="flex items-center gap-1">
              <Calendar className="w-3.5 h-3.5" />
              Hasta {formatFecha(campana.fecha_limite)}
            </span>
            <span className="flex items-center gap-1">
              <Clock className="w-3.5 h-3.5" />
              {campana.duracion_cita} min · {campana.horario_inicio}–{campana.horario_fin}
            </span>
          </p>
        </div>
        <div className="shrink-0 flex items-center gap-2 text-amber-700 text-sm font-medium">
          Reservar
          <ChevronRight className="w-4 h-4" />
        </div>
      </div>
    </button>
  );
}

function FilaReservada({ campana, onCancelar, onReagendar }) {
  const c = campana.cita;
  const [cond, setCond] = useState(null);

  // Fase 14b · #12 (MODO B) — condiciones actuales de la cita.
  useEffect(() => {
    let vivo = true;
    if (c?.id) {
      getCondicionesCita(c.id)
        .then((d) => { if (vivo) setCond(d); })
        .catch(() => { if (vivo) setCond(null); });
    }
    return () => { vivo = false; };
  }, [c?.id]);

  // Flags: preferimos el endpoint dedicado; si aún no cargó (o falló), caemos a
  // los flags que ya trae el listado para no bloquear la UI.
  const puedeCancelar = cond ? cond.puede_cancelar : (campana.puede_cancelar_cita !== false);
  const puedeReagendar = cond
    ? cond.puede_reagendar
    : (campana.puede_cancelar_cita !== false && campana.cancelaciones_restantes !== 0);
  const motivo = cond?.motivo_no_puede || null;

  return (
    <Card className="p-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <CheckCircle2 className="w-4 h-4 text-emerald-600" />
            <span className="font-medium text-slate-900">{campana.titulo}</span>
          </div>
          <p className="text-sm text-slate-700 mt-1">
            {formatFechaLarga(c.fecha)} · <strong>{c.hora_inicio}</strong>–{c.hora_fin}
          </p>
          <div className="mt-2 flex items-center gap-2 flex-wrap text-xs">
            <Badge variant="success">{c.estado}</Badge>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <Button
            variant="outline"
            size="sm"
            onClick={onReagendar}
            disabled={!puedeReagendar}
            title={!puedeReagendar ? (motivo || 'No disponible') : 'Reagendar cita'}
          >
            <RefreshCw className="w-3.5 h-3.5" />
            Reagendar
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={onCancelar}
            disabled={!puedeCancelar}
            title={!puedeCancelar ? (motivo || 'No disponible') : 'Cancelar cita'}
          >
            <XCircle className="w-3.5 h-3.5" />
            Cancelar
          </Button>
        </div>
      </div>

      {cond && (
        <div className="mt-3">
          <CondicionesCancelacion condiciones={cond} />
        </div>
      )}
    </Card>
  );
}

function FilaHistorico({ campana }) {
  const meta = ESTADO_USUARIO_LABEL[campana.estado_usuario] || { label: campana.estado_usuario };
  return (
    <div className="border border-slate-200 bg-white rounded-lg p-3 flex items-center justify-between">
      <div className="min-w-0">
        <div className="text-sm text-slate-700 font-medium truncate">{campana.titulo}</div>
        <div className="text-xs text-slate-400 mt-0.5">
          {campana.fecha_inicio || '?'} – {campana.fecha_limite || '?'} · Campaña{' '}
          <em>{campana.estado}</em>
        </div>
      </div>
      <span className="text-xs px-2 py-0.5 rounded-full whitespace-nowrap text-slate-600 bg-slate-50 border border-slate-200">
        {meta.label}
      </span>
    </div>
  );
}

function ModalCancelar({ campana, reagendar, loading, error, onCancelar, onConfirmar }) {
  const c = campana.cita;
  const restantes = campana.cancelaciones_restantes;

  return (
    <div
      className="fixed inset-0 bg-slate-900/40 z-50 grid place-items-center px-4"
      onClick={loading ? undefined : onCancelar}
    >
      <div
        className="bg-white rounded-lg shadow-lg max-w-md w-full p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-sm font-semibold text-slate-900 mb-2">
          {reagendar ? 'Reagendar tu cita' : 'Cancelar tu cita'}
        </h3>
        <p className="text-sm text-slate-600">
          {reagendar
            ? 'Vamos a cancelar tu cita actual y abrir el flujo para que elijas un nuevo horario.'
            : 'Vas a cancelar la siguiente cita:'}
        </p>

        <div className="mt-3 p-3 bg-slate-50 border border-slate-100 rounded-md text-sm">
          <div className="font-medium text-slate-800">{campana.titulo}</div>
          <div className="text-slate-600 mt-0.5">
            {formatFechaLarga(c.fecha)} · {c.hora_inicio}–{c.hora_fin}
          </div>
        </div>

        {restantes !== null && restantes !== undefined && (
          <div className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-md p-2.5">
            {restantes > 1 && (
              <>Esta cancelación cuenta. Te quedarán <strong>{restantes - 1}</strong> cancelaciones tras esta.</>
            )}
            {restantes === 1 && (
              <>Esta es tu <strong>última</strong> cancelación disponible. Si no reservas después, no podrás volver a hacerlo en esta campaña.</>
            )}
            {restantes === 0 && (
              <>Ya has usado todas las cancelaciones permitidas en esta campaña.</>
            )}
          </div>
        )}

        {error && (
          <div className="mt-3 text-xs text-red-700 bg-red-50 border border-red-100 rounded-md p-2.5 flex items-start gap-1.5">
            <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            {error}
          </div>
        )}

        <div className="mt-4 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCancelar}
            disabled={loading}
            className="px-3 py-1.5 text-sm rounded-lg border border-slate-200 hover:bg-slate-50 disabled:opacity-50"
          >
            Volver
          </button>
          <button
            type="button"
            onClick={onConfirmar}
            disabled={loading || restantes === 0}
            className="px-3 py-1.5 text-sm rounded-lg text-white bg-red-600 hover:bg-red-700 disabled:opacity-60 flex items-center gap-1.5"
          >
            {loading && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {reagendar ? 'Cancelar y reagendar' : 'Cancelar cita'}
          </button>
        </div>
      </div>
    </div>
  );
}

function formatFecha(s) {
  if (!s) return '—';
  return new Date(s).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' });
}
function formatFechaLarga(s) {
  if (!s) return '—';
  return new Date(s).toLocaleDateString('es-ES', {
    weekday: 'long', day: 'numeric', month: 'long',
  });
}
